import {
    Button,
    Grid,
    TextField,
    Typography,
    Box,
    IconButton,
    Autocomplete,
    Chip,
    Checkbox,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Loadable } from "../../../common";
import { ContractsSearchComponent } from "../../contractsPage/ContractsSearchComponent";
import { Portlet, PortletBody, PortletHeader } from "../../../partials/content/Portlet";
import Helmet from "react-helmet";
import * as SUPPLEMENTARY_PROMPTS_API from "../../../store/api/supplementaryPrompts";

const useStyles = makeStyles(theme => ({
    textField: {
        width: "100%"
    },
    promptField: {
        width: "100%",
        fontFamily: "monospace",
        backgroundColor: 'white',
        padding: theme.spacing(2),
    },
    documentPreviewSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
}));

interface SupplementaryPromptEditPageProps {
    intl: any;
    history: any;
    tinyContracts: any[];
    actions: {
        getTinyContracts: () => void;
        resetSelectedContracts: () => void;
        setSelectedContracts: (ids: string[]) => void;
    };
}

export default function SupplementaryPromptEditPage(props: SupplementaryPromptEditPageProps) {
    const {
        intl,
        history,
        tinyContracts,
        actions: { getTinyContracts, resetSelectedContracts, setSelectedContracts }
    } = props;

    const { id } = useParams<{ id?: string }>();
    const isEditMode = !!id;
    const classes = useStyles();
    const [loading, setLoading] = useState(isEditMode);
    const [promptData, setPromptData] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = React.useRef<HTMLFormElement>(null);

    // Configuration for field editability
    // All fields are now editable in both add and edit modes
    const fieldConfig = {
        contract: true,      // Always editable
        description: true,   // Always editable
        prompt: true         // Always editable
    };

    // Fetch prompt data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            setLoading(true);
            SUPPLEMENTARY_PROMPTS_API.getSuppPromptDocument(parseInt(id))
                .then((response: any) => {
                    setPromptData(response);
                    // Set selected contracts (multi-select) - extract from supplementalPromptContractXref
                    if (response.supplementalPromptContractXref && response.supplementalPromptContractXref.length > 0) {
                        const contractIds = response.supplementalPromptContractXref.map(
                            (item: any) => item.contractId.toString()
                        );
                        setSelectedContracts(contractIds);
                    }
                })
                .catch((error: any) => {
                    console.error("Error fetching prompt:", error);
                    toast.error("Failed to load prompt data", {
                        autoClose: 5000,
                    });
                    history.goBack();
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isEditMode, id]);

    useEffect(() => {
        getTinyContracts();
        return () => {
            resetSelectedContracts();
        };
    }, [getTinyContracts]);

    const handleBack = () => {
        history.goBack();
    };

    // Get contract objects for initial values in edit mode
    const getInitialContracts = () => {
        if (!isEditMode || !promptData?.supplementalPromptContractXref) {
            return [];
        }
        
        const contractIds = promptData.supplementalPromptContractXref.map(
            (item: any) => item.contractId
        );
        
        return tinyContracts.filter((contract: any) => 
            contractIds.includes(contract.id)
        );
    };

    const initialValues = {
        contracts: getInitialContracts(),
        description: promptData?.description || '',
        prompt: promptData?.prompt || '',
    };

    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        setIsSubmitting(true);

        try {
            // Validate required fields
            if (!values.contracts || values.contracts.length === 0) {
                toast.error("Please select at least one contract", {
                    autoClose: 5000,
                });
                setSubmitting(false);
                setIsSubmitting(false);
                return;
            }

            if (!values.description || values.description.trim() === '') {
                toast.error("Please enter a description", {
                    autoClose: 5000,
                });
                setSubmitting(false);
                setIsSubmitting(false);
                return;
            }

            if (!values.prompt || values.prompt.trim() === '') {
                toast.error("Please enter a prompt", {
                    autoClose: 5000,
                });
                setSubmitting(false);
                setIsSubmitting(false);
                return;
            }

            // Validate the prompt before saving (use first contract for validation)
            try {
                const validationResponse = await SUPPLEMENTARY_PROMPTS_API.validateSuppPrompt({
                    prompt_text: values.prompt.trim(),
                    contract_id: values.contracts[0].id
                });

                // If validation fails, show error and stop execution
                if (validationResponse?.valid === false) {
                    const violations = validationResponse?.hipaa_violations || [];
                    const conflicts = validationResponse?.conflicts || [];
                    const message = validationResponse?.message || "Prompt validation failed";
                    
                    let errorMessage = message;
                    if (violations.length > 0) {
                        errorMessage += `\nHIPAA Violations: ${violations.join(', ')}`;
                    }
                    if (conflicts.length > 0) {
                        errorMessage += `\nConflicts: ${conflicts.join(', ')}`;
                    }
                    
                    toast.error(errorMessage, {
                        autoClose: 7000,
                    });
                    
                    setSubmitting(false);
                    setIsSubmitting(false);
                    return;
                }
                
                // Validation passed - proceed with save
            } catch (validationError: any) {
                // If validation API fails, show error and stop execution
                console.error("Validation error:", validationError);
                const errorMsg = validationError?.payload?.errorDetails || 
                                validationError?.message || 
                                "Failed to validate prompt. Please try again.";
                toast.error(errorMsg, {
                    autoClose: 5000,
                });
                setSubmitting(false);
                setIsSubmitting(false);
                return;
            }

            const payload = {
                ...(isEditMode && { id: parseInt(id!) }),
                contractIds: values.contracts.map((contract: any) => contract.id),
                prompt: values.prompt.trim(),
                description: values.description.trim(),
                isActive: true, // Validation passed, so set to active
            };

            if (isEditMode) {
                await SUPPLEMENTARY_PROMPTS_API.updateSuppPromptDocument(payload);
                toast.success("Supplementary prompt updated successfully", {
                    autoClose: 5000,
                });
            } else {
                await SUPPLEMENTARY_PROMPTS_API.createSuppPromptDocument(payload);
                toast.success("Supplementary prompt created successfully", {
                    autoClose: 5000,
                });
            }

            history.push("/cms/supplementary-prompts");
        } catch (error: any) {
            console.error("Error saving prompt:", error);
            toast.error(error?.message || "Failed to save prompt", {
                autoClose: 5000,
            });
        } finally {
            setSubmitting(false);
            setIsSubmitting(false);
        }
    };

    return (
        <Loadable loading={loading}>
            <Helmet title={isEditMode ? "Edit Supplementary Prompt" : "Add Supplementary Prompt"} />
            <Portlet>
                <PortletHeader
                    title={isEditMode ? "Edit Supplementary Prompt" : "Add Supplementary Prompt"}
                    toolbar={
                        <Box display="flex" gap={1} py={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    if (formRef.current) {
                                        formRef.current.dispatchEvent(
                                            new Event('submit', { cancelable: true, bubbles: true })
                                        );
                                    }
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "SAVING..." : "SAVE"}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                disabled={isSubmitting}
                            >
                                BACK
                            </Button>
                        </Box>
                    }
                />
                <PortletBody>
                    <Formik
                        enableReinitialize={true}
                        initialValues={initialValues}
                        validate={values => {
                            const errors: any = {};

                            // Description validation
                            if (!values.description || !values.description.trim()) {
                                errors.description = "Description is required";
                            } else if (values.description.length > 500) {
                                errors.description = "Description cannot exceed 500 characters";
                            }

                            // Contracts validation
                            if (values.contracts.length === 0) {
                                errors.contracts = "Select at least one contract";
                            }

                            // Prompt validation
                            if (!values.prompt || !values.prompt.trim()) {
                                errors.prompt = "Prompt is required";
                            }

                            return errors;
                        }}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form ref={formRef} onSubmit={handleSubmit} noValidate autoComplete="off">
                                <Grid container spacing={3}>
                                    {/* Contracts Selection - Multi-select */}
                                    <Grid item xs={6} className="contract-selection">
                                        <Box sx={{ width: '100%' }}>
                                            <ContractsSearchComponent
                                                isMulti={true}
                                                handleSetContract={(selected) =>
                                                    setFieldValue(
                                                        "contracts",
                                                        [...tinyContracts]
                                                            .filter((item) => selected?.includes(String(item?.id)))
                                                            .sort(
                                                                (a, b) =>
                                                                    selected.indexOf(String(a?.id)) - selected.indexOf(String(b?.id))
                                                            )
                                                    )
                                                }
                                                icon={
                                                    <Autocomplete
                                                        onKeyDown={(e) => { e.preventDefault(); }}
                                                        onChange={(obj, updatedContracts, event) => {
                                                            if (event === "clear") {
                                                                setFieldValue("contracts", [])
                                                                resetSelectedContracts()
                                                            }
                                                            if (event === "removeOption") {
                                                                setFieldValue("contracts", updatedContracts)
                                                                setSelectedContracts(updatedContracts?.map((item: any) => String(item?.id)))
                                                            }
                                                        }}
                                                        open={false}
                                                        value={values.contracts}
                                                        multiple
                                                        options={tinyContracts}
                                                        disableCloseOnSelect
                                                        getOptionLabel={(option: any) => option.name}
                                                        sx={{ width: '100%' }}
                                                        renderOption={(props: any, option: any, { selected }) => (
                                                            <li {...props}>
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                    style={{ marginRight: 8 }}
                                                                    checked={selected}
                                                                />
                                                                {option?.name}
                                                            </li>
                                                        )}
                                                        renderTags={(tagValue, getTagProps) => {
                                                            return (
                                                                <Typography variant="caption">
                                                                    {tagValue?.map((option: any, index: number) => (
                                                                        <Chip
                                                                            size="small"
                                                                            color="secondary"
                                                                            key={index}
                                                                            label={option.name}
                                                                            {...getTagProps({ index })}
                                                                        />
                                                                    ))}
                                                                </Typography>
                                                            )
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Contracts*"
                                                                margin="normal"
                                                                fullWidth
                                                                error={Boolean(touched.contracts && errors.contracts)}
                                                                helperText={touched.contracts && errors.contracts}
                                                            />
                                                        )}
                                                    />
                                                }
                                            />
                                        </Box>
                                    </Grid>

                                    {/* Description */}
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Description *"
                                            name="description"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            disabled={!fieldConfig.description}
                                            error={Boolean(touched.description && errors.description)}
                                            helperText={touched.description && errors.description}
                                            placeholder="Enter a brief description of this supplementary prompt"
                                        />
                                    </Grid>

                                    {/* Prompt */}
                                    <Grid item xs={12}>
                                        <Box className={classes.documentPreviewSection}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ color: '#6c757d', mb: 2 }}>
                                                Prompt
                                            </Typography>
                                            <TextField
                                                name="prompt"
                                                className={classes.promptField}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.prompt}
                                                error={Boolean(touched.prompt && errors.prompt)}
                                                helperText={touched.prompt && errors.prompt}
                                                placeholder="Enter the supplementary prompt text..."
                                                multiline
                                                rows={5}
                                                fullWidth
                                            />
                                        </Box>
                                    </Grid>


                                </Grid>
                            </form>
                        )}
                    </Formik>
                </PortletBody>
            </Portlet>
        </Loadable>
    );
}
